import React, { useEffect, useState } from 'react';
import { ArrowRight, Download, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import config, { visiblePublications, featuredPublication, categories, photoFilter, TOPIC_TAGS, FORMAT_TAGS, getPublicationBySlug } from '../data/insightsConfig';
import { setCanonical } from '../lib/seo';
import { InsightCard, CategoryBadge, FeaturedHeroCard, FeaturedSideCard } from '../components/insights/InsightCard';

const serif = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 };
const CONTAINER = 'max-w-[1400px] mx-auto px-6 md:px-10';

// Manually pinned hero slot for the "Editor's Picks" row below. Confirmed 2026-08-26.
const PINNED_FOUNDER_STORY_SLUG = 'i-built-the-thing-that-would-have-saved-me';

const Insights = () => {
  const [active, setActive] = useState('All');
  const [activeTopic, setActiveTopic] = useState(null);
  const [activeFormat, setActiveFormat] = useState(null);
  const [dynamicPicks, setDynamicPicks] = useState([]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Insights | Archen™';
    setCanonical('/insights');
    return () => { document.title = prev; };
  }, []);

  const pinnedPub = getPublicationBySlug(PINNED_FOUNDER_STORY_SLUG);

  // Two dynamic "most-visited" slots alongside the pinned hero card. Falls back to the
  // most recent posts when there isn't enough real view data yet (e.g. a fresh preview).
  useEffect(() => {
    const exclude = [pinnedPub?.slug, featuredPublication?.slug].filter(Boolean).join(',');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/most-viewed?limit=2&exclude=${exclude}`)
      .then((r) => r.json())
      .then((data) => {
        const picks = (data.posts || []).map((p) => getPublicationBySlug(p.slug)).filter(Boolean);
        if (picks.length < 2) {
          const usedIds = new Set([pinnedPub?.id, featuredPublication?.id, ...picks.map((p) => p.id)]);
          const fallback = visiblePublications.filter((p) => !usedIds.has(p.id) && p.category !== 'White Paper');
          picks.push(...fallback.slice(0, 2 - picks.length));
        }
        setDynamicPicks(picks.slice(0, 2));
      })
      .catch(() => {
        const usedIds = new Set([pinnedPub?.id, featuredPublication?.id]);
        setDynamicPicks(visiblePublications.filter((p) => !usedIds.has(p.id) && p.category !== 'White Paper').slice(0, 2));
      });
  }, []);

  // Clicking a tag (from a card or the filter row) toggles that filter independently
  // of the other axis; clicking an already-active tag clears it.
  const onTagClick = (kind, value) => {
    if (kind === 'topic') setActiveTopic((cur) => (cur === value ? null : value));
    else setActiveFormat((cur) => (cur === value ? null : value));
  };

  // Featured piece is presented separately; grid shows the rest, filtered. The pinned
  // founder story and the two "most read" picks already appear in the row above, so
  // they're left out of the grid to avoid showing the same card twice on one page.
  const featuredRowIds = new Set([pinnedPub?.id, ...dynamicPicks.map((p) => p.id)].filter(Boolean));
  const rest = visiblePublications.filter((p) => p.id !== featuredPublication?.id && !featuredRowIds.has(p.id));
  const filtered = rest
    .filter((p) => active === 'All' || p.category === active)
    .filter((p) => !activeTopic || (p.topicTags || []).includes(activeTopic))
    .filter((p) => !activeFormat || p.formatTag === activeFormat);

  return (
    <div className="bg-[#FDF8F3] min-h-screen" data-testid="insights-page" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-14 md:pb-20">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: intro + subscribe */}
            <div>
              <p className="text-[#7A5A2E] text-[11px] tracking-[0.24em] uppercase mb-7">Archen™ Insights</p>
              <h1
                className="text-[#1F1812]"
                style={{ ...serif, fontSize: 'clamp(3rem, 6vw, 5.4rem)', lineHeight: 0.98, letterSpacing: '-0.03em' }}
                data-testid="insights-headline"
              >
                What firms that don't lose disputes read.
              </h1>
              <p className="mt-8 text-[#3D3A36] text-[17px] md:text-[19px] leading-[1.6] max-w-[520px]">
                Research and original thinking on how design firms keep better records, and why it matters
                when a project goes wrong.
              </p>
              <div className="mt-9">
                <a
                  href="https://archen.beehiiv.com/subscribe"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="insights-subscribe"
                  className="inline-flex items-center gap-2 bg-[#1F1812] text-white text-[14px] font-medium px-7 py-3.5 rounded-lg transition-all duration-300 hover:bg-[#0F0D0A]"
                >
                  Subscribe to Paper Trail
                </a>
                <p data-testid="insights-subscribe-note" className="mt-3 text-[#7A5A2E] text-[13px]">
                  Research and frameworks on project knowledge, a few times a month. No spam.
                </p>
              </div>
            </div>

            {/* Right: featured white-paper cover card */}
            {featuredPublication && (
              <Link
                to={`/insights/${featuredPublication.slug}`}
                data-testid="insights-featured"
                className="group block rounded-[24px] overflow-hidden border border-[#2A2018]/[0.1] bg-[#2A2018] shadow-[0_30px_70px_-30px_rgba(42,32,24,0.5)] transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={featuredPublication.coverImage}
                    alt={featuredPublication.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    style={{ filter: photoFilter }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A2018] via-[#2A2018]/20 to-transparent" />
                  <div className="absolute top-5 left-5"><CategoryBadge category={featuredPublication.category} /></div>
                </div>
                <div className="p-7 md:p-9">
                  <p className="text-[#C9A567] text-[11px] tracking-[0.22em] uppercase mb-3">{featuredPublication.subtitle}</p>
                  <h2
                    className="text-white"
                    style={{ ...serif, fontSize: 'clamp(1.5rem, 2.3vw, 2.15rem)', lineHeight: 1.08, letterSpacing: '-0.01em', textWrap: 'balance' }}
                    data-testid="insights-featured-title"
                  >
                    {featuredPublication.title}
                  </h2>
                  <span
                    data-testid="insights-featured-read"
                    className="mt-6 inline-flex items-center gap-2 bg-[#F5EDE1] text-[#1F1812] group-hover:bg-white text-[14px] font-semibold px-6 py-3 rounded-lg transition-all duration-300"
                  >
                    <BookOpen size={15} /> Read White Paper
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Editor's Picks: pinned founder story (hero) + 2 dynamic "most read" cards */}
      {pinnedPub && (
        <section className="pb-14 md:pb-20" data-testid="insights-editors-picks">
          <div className={CONTAINER}>
            <p className="text-[#7A5A2E] text-[11px] tracking-[0.24em] uppercase mb-6">Editor's Picks</p>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-[60%]">
                <FeaturedHeroCard pub={pinnedPub} />
              </div>
              <div className="lg:w-[40%] flex flex-col gap-5">
                {dynamicPicks.map((p) => <FeaturedSideCard key={p.id} pub={p} />)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="pb-10">
        <div className={CONTAINER}>
          <div className="flex flex-wrap items-center gap-2.5 border-t border-[#2A2018]/[0.08] pt-8">
            {categories.filter((c) => c.active).map((c) => (
              <button
                key={c.value}
                disabled={!c.active}
                onClick={() => c.active && setActive(c.value)}
                data-testid={`insights-filter-${c.value.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-[13px] font-medium px-4 py-2 rounded-full border transition-all duration-200 ${
                  !c.active
                    ? 'border-[#2A2018]/[0.06] text-[#B4A48C] cursor-not-allowed'
                    : active === c.value
                    ? 'bg-[#1F1812] text-white border-[#1F1812]'
                    : 'bg-transparent text-[#5C4F42] border-[#2A2018]/[0.14] hover:border-[#2A2018]/[0.3]'
                }`}
              >
                {c.label}{!c.active && ' · soon'}
              </button>
            ))}
          </div>

          {/* Topic + format tag filters, independent of each other and of the category filter above. */}
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="insights-topic-filters">
            <span className="text-[#7A5A2E] text-[11px] tracking-[0.16em] uppercase mr-1">Topic</span>
            {TOPIC_TAGS.map((t) => (
              <button
                key={t}
                onClick={() => onTagClick('topic', t)}
                data-testid={`insights-topic-filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`text-[12.5px] font-medium px-3.5 py-1.5 rounded-full border transition-all duration-200 ${
                  activeTopic === t
                    ? 'bg-[#4C6A59] text-white border-[#4C6A59]'
                    : 'bg-transparent text-[#4C6A59] border-[#4C6A59]/30 hover:border-[#4C6A59]/60'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="insights-format-filters">
            <span className="text-[#7A5A2E] text-[11px] tracking-[0.16em] uppercase mr-1">Format</span>
            {FORMAT_TAGS.map((t) => (
              <button
                key={t}
                onClick={() => onTagClick('format', t)}
                data-testid={`insights-format-filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`text-[12.5px] font-medium px-3.5 py-1.5 rounded-full border transition-all duration-200 ${
                  activeFormat === t
                    ? 'bg-[#1F1812] text-white border-[#1F1812]'
                    : 'bg-transparent text-[#5C4F42] border-[#2A2018]/20 hover:border-[#2A2018]/40'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {(activeTopic || activeFormat) && (
            <div className="mt-3" data-testid="insights-tag-filters-clear-row">
              <button
                onClick={() => { setActiveTopic(null); setActiveFormat(null); }}
                data-testid="insights-tag-filters-clear"
                className="text-[12.5px] font-medium px-3.5 py-1.5 text-[#B4342B] hover:underline"
              >
                Clear tag filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Publication grid */}
      <section className="pb-24 md:pb-32">
        <div className={CONTAINER}>
          {filtered.length === 0 ? (
            <p className="text-[#7A5A2E] text-[15px] py-16 text-center">No publications match these filters.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="insights-grid">
              {filtered.map((p) => <InsightCard key={p.id} pub={p} onTagClick={onTagClick} />)}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Insights;
